import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Settings, Stethoscope, Smile, CreditCard, MessageSquare, Monitor, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  { value: 'medical_only', label: 'Medical Only', description: 'Standard medical practice management' },
  { value: 'dental_only', label: 'Dental Only', description: 'Dental practice management with charting' },
  { value: 'combined', label: 'Combined (Medical + Dental)', description: 'Full-service clinic with both modules' }
];

const PAYMENT_PROVIDERS = [
  { value: 'wipay', label: 'WiPay', description: 'Caribbean payment processor' },
  { value: 'ncb', label: 'NCB', description: 'National Commercial Bank Jamaica' },
  { value: 'fac', label: 'FAC', description: 'First Atlantic Commerce' },
  { value: 'powertranz', label: 'Powertranz', description: 'Regional payment gateway' }
];

const ClinicSettings = () => {
  const { token, isOwner } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenant/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/api/tenant/settings`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      toast.success('Settings updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const enableDental = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/tenant/enable-dental`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchSettings();
      toast.success('Dental module enabled with services catalog');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable dental');
    } finally {
      setSaving(false);
    }
  };

  const handleFeatureToggle = (feature, enabled) => {
    const newFeatures = { ...settings.features_enabled, [feature]: enabled };
    updateSettings({ features_enabled: newFeatures });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!isOwner) {
    return (
      <Layout>
        <div className="p-6">
          <div className="card p-8 text-center">
            <Settings className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Access Restricted</h2>
            <p className="text-slate-500">Only clinic owners can access settings.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Clinic Settings</h1>
          <p className="text-slate-500">Configure modules and features for your clinic</p>
        </div>

        {/* Plan Selection */}
        <div className="card p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Clinic Plan
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLANS.map(plan => (
              <div
                key={plan.value}
                onClick={() => updateSettings({ plan: plan.value })}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  settings?.plan === plan.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {plan.value.includes('medical') && <Stethoscope className="w-5 h-5 text-blue-600" />}
                  {plan.value.includes('dental') && <Smile className="w-5 h-5 text-teal-600" />}
                  {plan.value === 'combined' && (
                    <>
                      <Stethoscope className="w-5 h-5 text-blue-600" />
                      <Smile className="w-5 h-5 text-teal-600" />
                    </>
                  )}
                </div>
                <h3 className="font-medium text-slate-800">{plan.label}</h3>
                <p className="text-sm text-slate-500">{plan.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Modules */}
        <div className="card p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Active Modules</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Stethoscope className="w-6 h-6 text-blue-600" />
                <div>
                  <Label className="text-base">Medical Module</Label>
                  <p className="text-sm text-slate-500">Patient management, encounters, billing</p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded text-sm font-medium ${
                settings?.modules_enabled?.medical 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-slate-100 text-slate-500'
              }`}>
                {settings?.modules_enabled?.medical ? 'Enabled' : 'Disabled'}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Smile className="w-6 h-6 text-teal-600" />
                <div>
                  <Label className="text-base">Dental Module</Label>
                  <p className="text-sm text-slate-500">Dental scheduling, charting, treatment plans</p>
                </div>
              </div>
              {settings?.modules_enabled?.dental ? (
                <div className="px-3 py-1 rounded text-sm font-medium bg-green-100 text-green-700">
                  Enabled
                </div>
              ) : (
                <Button onClick={enableDental} disabled={saving}>
                  Enable Dental
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Add-on Features */}
        <div className="card p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Add-on Features</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-purple-600" />
                <div>
                  <Label className="text-base">Payment Processing</Label>
                  <p className="text-sm text-slate-500">Accept credit card payments</p>
                </div>
              </div>
              <Switch
                checked={settings?.features_enabled?.payments || false}
                onCheckedChange={(checked) => handleFeatureToggle('payments', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-6 h-6 text-green-600" />
                <div>
                  <Label className="text-base">SMS/Texting</Label>
                  <p className="text-sm text-slate-500">Two-way patient communication via Twilio</p>
                </div>
              </div>
              <Switch
                checked={settings?.features_enabled?.texting || false}
                onCheckedChange={(checked) => handleFeatureToggle('texting', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Monitor className="w-6 h-6 text-orange-600" />
                <div>
                  <Label className="text-base">Kiosk Mode</Label>
                  <p className="text-sm text-slate-500">Patient self-service check-in</p>
                </div>
              </div>
              <Switch
                checked={settings?.features_enabled?.kiosk || false}
                onCheckedChange={(checked) => handleFeatureToggle('kiosk', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <ClipboardList className="w-6 h-6 text-blue-600" />
                <div>
                  <Label className="text-base">Advanced Charting</Label>
                  <p className="text-sm text-slate-500">Interactive dental charting with SVG diagrams</p>
                </div>
              </div>
              <Switch
                checked={settings?.features_enabled?.charting || false}
                onCheckedChange={(checked) => handleFeatureToggle('charting', checked)}
              />
            </div>
          </div>
        </div>

        {/* Payment Provider */}
        {settings?.features_enabled?.payments && (
          <div className="card p-6 mb-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment Provider
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {PAYMENT_PROVIDERS.map(provider => (
                <div
                  key={provider.value}
                  onClick={() => updateSettings({ payment_provider: provider.value })}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                    settings?.payment_provider === provider.value
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <h3 className="font-medium text-slate-800">{provider.label}</h3>
                  <p className="text-xs text-slate-500">{provider.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Current Configuration Summary */}
        <div className="card p-6 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Configuration Summary</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Plan:</span>
              <span className="ml-2 font-medium">{settings?.plan?.replace('_', ' ')}</span>
            </div>
            <div>
              <span className="text-slate-500">Medical:</span>
              <span className={`ml-2 font-medium ${settings?.modules_enabled?.medical ? 'text-green-600' : 'text-slate-400'}`}>
                {settings?.modules_enabled?.medical ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <span className="text-slate-500">Dental:</span>
              <span className={`ml-2 font-medium ${settings?.modules_enabled?.dental ? 'text-green-600' : 'text-slate-400'}`}>
                {settings?.modules_enabled?.dental ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <span className="text-slate-500">Payment Provider:</span>
              <span className="ml-2 font-medium">{settings?.payment_provider || 'Not configured'}</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClinicSettings;
